import React from 'react';

import { AddressType } from 'models/shipments/Payload';

import { config } from '../../../config/env-config';
import { apiEndpoint, extLnkPostOfficeLocatorWithShowpop } from '../../../config/track-config';
import { Notification, NotificationStatus, NotificationType } from '../../../models/Notification';
import { clearNotification, createNotification } from '../../../store/notification';
import { AppDispatch, useAppDispatch } from '../../../store/store';
import axiosInstance from '../../../utils/axios.utils';
import { errorContent } from '../../common/Notification/Notification.config';
import { useCurrentArticle } from './shipment.hooks';

const COLLECTION_POINT_TYPE_MAPPING: { [key: string]: string } = {
  PL: 'UPL',
  PC: 'PO,DC',
};
export const getLocationV3WorkCentre = (dpid: string, collectionPointType: string) => {
  return axiosInstance.get(apiEndpoint.locationWorkCentre, {
    params: {
      'location[addresses.attributes.dpid]': dpid,
      type: COLLECTION_POINT_TYPE_MAPPING[collectionPointType],
    },
    headers: {
      'Auth-Key': config.api.locations,
    },
  });
};

export enum ELoadingStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  COMPLETED = 'completed',
}

export const useLocationByDpid = (autoFetch = true) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = React.useState<ELoadingStatus>(ELoadingStatus.IDLE);

  const currentArticle = useCurrentArticle();
  const dpid = currentArticle?.address?.deliveryPointId?.toString() || '';
  const addressType = currentArticle?.address?.addressType || null;

  const isPLPC = addressType === AddressType.PARCEL_LOCKER || addressType === AddressType.PARCEL_COLLECT;

  const fetchLocation = React.useCallback(async () => {
    if (!dpid || !isPLPC) {
      setLoading(ELoadingStatus.IDLE);
      return;
    }

    setLoading(ELoadingStatus.LOADING);
    try {
      const response = await getLocationV3WorkCentre(dpid, addressType);
      const workCentreId = response?.data?.workcentres?.[0]?.attributes?.work_centre_id || null;
      if (!workCentreId) return displayLocationError(dispatch);
      const polLink = `${extLnkPostOfficeLocatorWithShowpop}${workCentreId}`;
      window.open(polLink, '_blank', 'noopener,noreferrer');
    } catch (err) {
      displayLocationError(dispatch);
    } finally {
      setLoading(ELoadingStatus.COMPLETED);
    }
  }, [dpid, addressType, isPLPC, dispatch]);

  React.useEffect(() => {
    if (autoFetch) fetchLocation();
  }, [dpid, isPLPC, autoFetch, fetchLocation]);

  return { loading, fetchLocation };
};

const displayLocationError = (dispatch: AppDispatch) => {
  const notification: Notification = {
    type: NotificationType.ALERT,
    status: NotificationStatus.ERROR,
    message: errorContent.failedToFetchLocation,
  };
  dispatch(clearNotification());
  return dispatch(createNotification(notification));
};
